import {
  findSurfaceRangeByLabel,
  matchSurfaceRangeFromValue,
  surfaceRangeOptions,
  surfaceRangeToNumeric,
} from "../domain/surfaceRanges";
import quoteRequestOptions from "../domain/quoteRequestOptions.json";

export const PROJECT_OPTIONS = quoteRequestOptions.projects;

export const PROJECT_SERVICE_OPTIONS = quoteRequestOptions.servicesByProject;

export const MAX_DEMANDE_LIBRE_LENGTH = 300;
export const MAX_ADDRESS_LENGTH = 300;
export const MAX_CONTACT_NAME_LENGTH = 100;
export const MAX_EMAIL_LENGTH = 254;
export const MAX_CITY_LENGTH = 120;
export const MAX_UTM_LENGTH = 100;

export const TYPE_BIEN_OPTIONS = quoteRequestOptions.propertyTypes;

export const CIVILITE_OPTIONS = quoteRequestOptions.civilities;

export const stepDefinitions = [
  {
    id: "projet",
    title: "Quel est votre projet immobilier ?",
    description: "Sélectionnez une option.",
    field: "projet",
    type: "project-choice",
    options: PROJECT_OPTIONS,
  },
  {
    id: "service",
    title: "",
    description: "",
    type: "service-request",
  },
  {
    id: "bien",
    title: "Informations sur le bien",
    description: "Renseignez le type de bien, son adresse et sa surface.",
    type: "property",
    fields: [
      {
        id: "type_bien",
        field: "type_bien",
        label: "Type de bien",
        type: "choice",
        options: TYPE_BIEN_OPTIONS,
        required: true,
      },
      {
        id: "adresse",
        field: "adresse",
        label: "Adresse",
        type: "address",
        required: true,
        placeholder: "13 rue Exemple, 75000 Paris",
      },
      {
        id: "surface",
        field: "surface_range",
        label: "Surface",
        type: "surface-range",
        options: surfaceRangeOptions,
        required: true,
      },
    ],
  },
  {
    id: "contact",
    title: "Vos informations",
    description: "Pour obtenir votre devis",
    field: "contact",
    type: "contact",
  },
];

export const initialFormData = (defaults = {}) => ({
  prestations: [],
  projet: "",
  demande_libre: "",
  type_bien: "",
  adresse: "",
  adresse_normalisee: "",
  code_postal: "",
  ville: "",
  code_insee: "",
  latitude: null,
  longitude: null,
  surface_m2: "",
  surface_range: null,
  civilite: "",
  prenom: "",
  nom: "",
  telephone: "",
  email: "",
  utm_source: defaults.utm_source || "",
  utm_medium: defaults.utm_medium || "",
  utm_campaign: defaults.utm_campaign || "",
});

export function resolveStepDefinition(step, formData = {}) {
  if (step.type !== "service-request") return step;

  if (formData.projet === "Autre") {
    return {
      ...step,
      title: "Veuillez préciser votre demande",
      description: "",
      field: "demande_libre",
      options: [],
      selectionMode: "text",
    };
  }

  const isMultiple = formData.projet === "Construction neuve";

  return {
    ...step,
    title: isMultiple
      ? "Sélectionnez la ou les prestations pour lesquelles vous souhaitez un devis personnalisé"
      : "Sélectionnez la prestation pour laquelle vous souhaitez un devis personnalisé",
    description: isMultiple ? "Plusieurs choix possibles" : "Un seul choix possible",
    field: "prestations",
    options: PROJECT_SERVICE_OPTIONS[formData.projet] || [],
    selectionMode: isMultiple ? "multiple" : "single",
  };
}

function validatePrestations(value, projet) {
  const allowedOptions = PROJECT_SERVICE_OPTIONS[projet];
  if (!allowedOptions || !Array.isArray(value)) {
    return "Veuillez sélectionner au moins une prestation";
  }

  const uniqueValues = new Set(value);
  const everyValueIsAllowed =
    uniqueValues.size === value.length && value.every((item) => allowedOptions.includes(item));
  const hasValidSelection =
    projet === "Construction neuve"
      ? value.length > 0
      : value.length === 1;

  return everyValueIsAllowed && hasValidSelection
    ? undefined
    : "Veuillez sélectionner au moins une prestation";
}

export function validateField(key, value, formData = {}) {
  if (key === "projet") {
    return PROJECT_OPTIONS.includes(value) ? undefined : "Sélectionnez votre projet.";
  }

  if (key === "prestations") {
    return validatePrestations(value, formData.projet);
  }

  if (key === "demande_libre") {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    return normalizedValue.length > 0 && normalizedValue.length <= MAX_DEMANDE_LIBRE_LENGTH
      ? undefined
      : "Précisez votre demande.";
  }

  if (key === "type_bien") {
    return TYPE_BIEN_OPTIONS.includes(value) ? undefined : "Sélectionnez un type de bien.";
  }

  if (key === "adresse") {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    return normalizedValue && normalizedValue.length <= MAX_ADDRESS_LENGTH
      ? undefined
      : "Saisissez une adresse valide.";
  }

  if (key === "civilite") {
    return CIVILITE_OPTIONS.includes(value) ? undefined : "Sélectionnez une civilité.";
  }

  if (key === "prenom") {
    return typeof value === "string" &&
      value.trim() &&
      value.trim().length <= MAX_CONTACT_NAME_LENGTH
      ? undefined
      : "Saisissez votre prénom.";
  }

  if (key === "nom") {
    return typeof value === "string" &&
      value.trim() &&
      value.trim().length <= MAX_CONTACT_NAME_LENGTH
      ? undefined
      : "Saisissez votre nom.";
  }

  if (key === "email") {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "Saisissez votre adresse e-mail.";
    if (normalizedValue.length > MAX_EMAIL_LENGTH) {
      return "Saisissez une adresse e-mail valide.";
    }
    const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalizedValue);
    return valid ? undefined : "Saisissez une adresse e-mail valide.";
  }

  if (key === "telephone") {
    const digitsOnly = String(value || "").replace(/\s/g, "");
    const valid = /^\d{10}$/.test(digitsOnly);
    return valid ? undefined : "Téléphone invalide";
  }

  if (key === "surface_range") {
    const canonical = findSurfaceRangeByLabel(value?.label);
    const isCanonical =
      canonical &&
      canonical.min === value.min &&
      canonical.max === value.max &&
      surfaceRangeToNumeric(canonical) === formData.surface_m2;
    return isCanonical ? undefined : "Surface invalide";
  }

  if (typeof value === "string" && value.trim() === "") {
    return "Ce champ est obligatoire";
  }

  return undefined;
}

export function validateStep(step, formData) {
  if (step.type === "contact") {
    const fields = ["civilite", "prenom", "nom", "telephone", "email"];
    const errors = fields.reduce((acc, field) => {
      const err = validateField(field, formData[field]);
      if (err) acc[field] = err;
      return acc;
    }, {});
    return errors;
  }

  if (step.type === "property") {
    return step.fields.reduce((acc, field) => {
      const err = validateField(field.field, formData[field.field], formData);
      if (err) acc[field.field] = err;
      return acc;
    }, {});
  }

  if (step.type === "service-request") {
    const field = formData.projet === "Autre" ? "demande_libre" : "prestations";
    const err = validateField(field, formData[field], formData);
    return err ? { [field]: err } : {};
  }

  const err = validateField(step.field, formData[step.field], formData);
  return err ? { [step.field]: err } : {};
}

function normalizeCoordinate(value) {
  if (value === null || value === "" || typeof value === "boolean") return null;
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

function normalizeStoredText(value, maximum) {
  if (typeof value === "string") return value;
  const normalized =
    typeof value === "number" && Number.isFinite(value) ? String(value) : "";
  return maximum ? normalized.slice(0, maximum) : normalized;
}

function normalizeBoundedText(value, maximum) {
  const normalized = normalizeStoredText(value);
  return normalized.slice(0, maximum);
}

function normalizeUtm(value) {
  return typeof value === "string" ? value.trim().slice(0, MAX_UTM_LENGTH) : "";
}

export function normalizeFormData(data = {}) {
  const source = data && typeof data === "object" && !Array.isArray(data) ? data : {};
  const surfaceSelection = normalizeSurfaceSelection({
    surface_range: source.surface_range,
    surface_m2: source.surface_m2,
  });

  const projet = PROJECT_OPTIONS.includes(source.projet) ? source.projet : "";
  const typeBien = TYPE_BIEN_OPTIONS.includes(source.type_bien) ? source.type_bien : "";
  const latitude = normalizeCoordinate(source.latitude);
  const longitude = normalizeCoordinate(source.longitude);
  const hasCoordinatePair =
    latitude !== null &&
    longitude !== null &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;
  const codePostal = normalizeStoredText(source.code_postal).trim();
  const codeInsee = normalizeStoredText(source.code_insee).trim().toUpperCase();

  const next = {
    prestations: [],
    projet,
    demande_libre:
      typeof source.demande_libre === "string"
        ? source.demande_libre.slice(0, MAX_DEMANDE_LIBRE_LENGTH)
        : "",
    type_bien: typeBien,
    adresse: normalizeBoundedText(source.adresse, MAX_ADDRESS_LENGTH),
    adresse_normalisee: normalizeBoundedText(
      source.adresse_normalisee,
      MAX_ADDRESS_LENGTH
    ),
    code_postal: /^\d{5}$/.test(codePostal) ? codePostal : "",
    ville: normalizeBoundedText(source.ville, MAX_CITY_LENGTH),
    code_insee: /^[0-9A-Z]{5}$/.test(codeInsee) ? codeInsee : "",
    latitude: hasCoordinatePair ? latitude : null,
    longitude: hasCoordinatePair ? longitude : null,
    surface_m2: surfaceSelection.surface_m2,
    surface_range: surfaceSelection.surface_range,
    civilite: CIVILITE_OPTIONS.includes(source.civilite) ? source.civilite : "",
    prenom: normalizeBoundedText(source.prenom, MAX_CONTACT_NAME_LENGTH),
    nom: normalizeBoundedText(source.nom, MAX_CONTACT_NAME_LENGTH),
    telephone:
      typeof source.telephone === "string"
        ? source.telephone.replace(/\s/g, "").slice(0, 32)
        : "",
    email:
      typeof source.email === "string"
        ? source.email.trim().toLowerCase().slice(0, MAX_EMAIL_LENGTH)
        : "",
    utm_source: normalizeUtm(source.utm_source),
    utm_medium: normalizeUtm(source.utm_medium),
    utm_campaign: normalizeUtm(source.utm_campaign),
  };

  const allowedOptions = PROJECT_SERVICE_OPTIONS[next.projet] || [];
  const savedPrestations = Array.isArray(source.prestations) ? source.prestations : [];
  const compatiblePrestations = savedPrestations.filter(
    (item, index) =>
      allowedOptions.includes(item) && savedPrestations.indexOf(item) === index
  );

  if (next.projet === "Autre") {
    next.prestations = [];
  } else {
    next.prestations =
      next.projet === "Construction neuve"
        ? compatiblePrestations
        : compatiblePrestations.slice(0, 1);
    next.demande_libre = "";
  }

  return next;
}

export function normalizeSurfaceSelection(data = {}) {
  const next = { ...data };
  const maybeLabel = next.surface_range?.label || next.surface_range;
  const rangeFromLabel = findSurfaceRangeByLabel(maybeLabel);

  if (rangeFromLabel) {
    next.surface_range = { ...rangeFromLabel };
    next.surface_m2 = surfaceRangeToNumeric(rangeFromLabel);
    return next;
  }

  const rangeFromValue =
    matchSurfaceRangeFromValue(next.surface_m2) || matchSurfaceRangeFromValue(next.surface_range?.min);
  if (rangeFromValue) {
    next.surface_range = { ...rangeFromValue };
    next.surface_m2 = surfaceRangeToNumeric(rangeFromValue);
    return next;
  }

  next.surface_range = null;
  next.surface_m2 = "";
  return next;
}
